import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import React from 'react';

import { extractWizProperty } from '@/sections/poe/processors/wiz/helpers';
import type {
  WizParsedData,
  WizRenderContext,
} from '@/sections/poe/processors/wiz/types';

export function renderSecurityImpact(
  parsed: WizParsedData,
  ctx: WizRenderContext
): void {
  const { wizData } = parsed;
  const { sections, tempTabs, mainTabKeys } = ctx;

  if (
    wizData?.['configuration finding'] &&
    wizData?.['endpoint'] &&
    mainTabKeys.includes('INFORMATION')
  ) {
    const configFinding = wizData['configuration finding'];
    const endpoint = wizData['endpoint'];
    const currentRuntime =
      extractWizProperty(configFinding, 'currentConfig') ||
      'an outdated runtime';
    const endpointName = extractWizProperty(endpoint, 'name') || '';

    sections['wiz_security_impact'] = (
      <div className="space-y-6">
        {/* Risk Overview */}
        <div className="border-warning rounded-r-lg border-l-4 p-4">
          <div className="flex items-start">
            <ShieldExclamationIcon className="text-warning mt-1 mr-3 size-6 shrink-0" />
            <div>
              <h4 className="text-chariot-text-primary mb-2 text-lg font-semibold">
                Security Risk Assessment
              </h4>
              <p className="text-chariot-text-primary leading-relaxed">
                This resource is running <strong>{currentRuntime}</strong>,
                which has reached end-of-life and no longer receives security
                patches.
                {endpointName
                  ? ` The resource is publicly accessible via ${endpointName}, creating`
                  : ' This creates'}{' '}
                a potential attack vector for exploiting deprecated runtime
                vulnerabilities.
              </p>
            </div>
          </div>
        </div>

        {/* Impact Categories */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Technical Impact */}
          <div className="border-chariot-stroke rounded-lg border">
            <div className="border-chariot-stroke border-b px-4 py-2">
              <h5 className="text-error flex items-center font-semibold">
                <ExclamationTriangleIcon className="mr-2 size-4" />
                Technical Impact
              </h5>
            </div>
            <div className="space-y-3 p-4">
              <div className="flex items-start gap-3">
                <span className="bg-error mt-2 size-2 shrink-0 rounded-full"></span>
                <div>
                  <strong className="text-chariot-text-primary text-sm">
                    Deprecated Runtime Vulnerabilities
                  </strong>
                  <p className="text-chariot-text-secondary mt-1 text-xs">
                    Known CVEs in {currentRuntime} will not be patched
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="bg-error mt-2 size-2 shrink-0 rounded-full"></span>
                <div>
                  <strong className="text-chariot-text-primary text-sm">
                    Public API Exposure
                  </strong>
                  <p className="text-chariot-text-secondary mt-1 text-xs">
                    {endpointName
                      ? `Function accessible via ${endpointName}`
                      : 'Function is publicly accessible'}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="bg-warning mt-2 size-2 shrink-0 rounded-full"></span>
                <div>
                  <strong className="text-chariot-text-primary text-sm">
                    Compliance Risk
                  </strong>
                  <p className="text-chariot-text-secondary mt-1 text-xs">
                    Violates security best practices for runtime management
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Business Impact */}
          <div className="border-chariot-stroke rounded-lg border">
            <div className="border-chariot-stroke border-b px-4 py-2">
              <h5 className="text-warning flex items-center font-semibold">
                <InformationCircleIcon className="mr-2 size-4" />
                Business Impact
              </h5>
            </div>
            <div className="space-y-3 p-4">
              <div className="flex items-start gap-3">
                <span className="bg-error mt-2 size-2 shrink-0 rounded-full"></span>
                <div>
                  <strong className="text-chariot-text-primary text-sm">
                    Data Breach Risk
                  </strong>
                  <p className="text-chariot-text-secondary mt-1 text-xs">
                    Potential unauthorized access to function data and secrets
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="bg-warning mt-2 size-2 shrink-0 rounded-full"></span>
                <div>
                  <strong className="text-chariot-text-primary text-sm">
                    Service Disruption
                  </strong>
                  <p className="text-chariot-text-secondary mt-1 text-xs">
                    Potential denial of service or function compromise
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="bg-warning mt-2 size-2 shrink-0 rounded-full"></span>
                <div>
                  <strong className="text-chariot-text-primary text-sm">
                    Reputational Impact
                  </strong>
                  <p className="text-chariot-text-secondary mt-1 text-xs">
                    Security incidents from deprecated components
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Remediation Steps */}
        <div className="border-success rounded-r-lg border-l-4 p-4">
          <div className="flex items-start">
            <CheckCircleIcon className="text-success mt-1 mr-3 size-6 shrink-0" />
            <div className="grow">
              <h4 className="text-chariot-text-primary mb-3 text-lg font-semibold">
                Recommended Actions
              </h4>
              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <span className="bg-success inline-flex size-6 items-center justify-center rounded-full text-xs font-bold text-white">
                    1
                  </span>
                  <div>
                    <strong className="text-chariot-text-primary">
                      Update Runtime Immediately
                    </strong>
                    <p className="text-chariot-text-secondary text-sm">
                      Upgrade to a supported runtime version with active
                      security support
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="bg-success inline-flex size-6 items-center justify-center rounded-full text-xs font-bold text-white">
                    2
                  </span>
                  <div>
                    <strong className="text-chariot-text-primary">
                      Test Function Compatibility
                    </strong>
                    <p className="text-chariot-text-secondary text-sm">
                      Ensure application works correctly with updated runtime
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="bg-success inline-flex size-6 items-center justify-center rounded-full text-xs font-bold text-white">
                    3
                  </span>
                  <div>
                    <strong className="text-chariot-text-primary">
                      Review Access Controls
                    </strong>
                    <p className="text-chariot-text-secondary text-sm">
                      Verify API Gateway permissions and authentication
                      requirements
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
    tempTabs['INFORMATION'].push({
      label: 'Security Impact',
      value: 'wiz_security_impact',
    });
  }
}
